import { useState, useMemo, useEffect } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { getAuthHeaders } from "../../services/useAuth";
import { BASE_URL } from "../../config";

/* ─────────────────────────────────────────────────────────────── */
/* Constants */
/* ─────────────────────────────────────────────────────────────── */

const MONTHS = [
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
  "Jan",
  "Feb",
  "Mar",
];
const getCurrentFY = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = today.getMonth() + 1;

  const startYear = month >= 4 ? year : year - 1;
  const endYear = startYear + 1;

  return `FY ${startYear}-${String(endYear).slice(-2)}`;
};

const generateFYOptions = (count = 4) => {
  const currentFY = getCurrentFY();

  const currentStartYear = parseInt(
    currentFY.split(" ")[1].split("-")[0]
  );

  return Array.from({ length: count }, (_, i) => {
    const start = currentStartYear - i;
    const end = start + 1;
    return `FY ${start}-${String(end).slice(-2)}`;
  });
};

const FY_OPTIONS = generateFYOptions();


const STATE_COLORS = [
  "#2563EB", // bold blue
  "#DC2626", // bold red
  "#16A34A", // bold green
  "#D97706", // bold amber
  "#9333EA", // bold purple
  "#0891B2", // bold cyan
  "#EA580C", // bold orange
  "#4D7C0F", // bold olive-green
  "#DB2777", // bold pink
  "#1D4ED8", // deep blue
  "#B91C1C", // deep red
  "#065F46", // deep emerald
];

/* ─────────────────────────────────────────────────────────────── */
/* Helpers */
/* ─────────────────────────────────────────────────────────────── */

const calcPct = (a, t) => {
  if (!t && !a) return 0;
  if (!t && a > 0) return 100;
  return Math.round((a / t) * 100);
};

const statusColor = (p) =>
  p >= 100 ? "#16a34a" : p >= 80 ? "#d97706" : "#dc2626";

const fmtNum = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 10000000) return (n / 10000000).toFixed(2) + " Cr";
  if (n >= 100000) return (n / 100000).toFixed(2) + " L";
  if (n >= 1000) return (n / 1000).toFixed(1) + "K";
  return Number(n).toLocaleString("en-IN");
};

function CustomTooltip({ active, payload, label, rawRows, title, selectedFY }) {
  const stateMap = useMemo(() => {
    const map = {};
    rawRows?.forEach((r) => {
      map[r.state] = r;
    });
    return map;
  }, [rawRows]);

  if (!active || !payload?.length) return null;

  const fyParts = selectedFY?.split(" ")[1]?.split("-");
  const startYear = fyParts?.[0];
  const endYearShort = fyParts?.[1];
  const endYear = `20${endYearShort}`;

  const cleanLabel = String(label).trim();

  const monthYear = ["Jan", "Feb", "Mar"].includes(cleanLabel)
    ? endYear
    : startYear;

  return (
    <div
      style={{
        background: "#1e293b",
        border: "1px solid #334155",
        borderRadius: 6,
        padding: "8px 12px",
        fontSize: 11,
        color: "#f1f5f9",
        boxShadow: "0 4px 16px rgba(0,0,0,0.35)",
        minWidth: 240,
      }}
    >
      <div
        style={{
          fontWeight: 700,
          color: "#94a3b8",
          marginBottom: 8,
          fontSize: 12,
          borderBottom: "1px solid #334155",
          paddingBottom: 4,
        }}
      >
        {label} {monthYear} · {title}
      </div>

      {payload.map((entry, i) => {
        const row = stateMap[entry.name];
        const md = row?.month_data?.[label];
        const target = md?.target ?? 0;
        const achieved = md?.achieved ?? 0;
        const isPOB = title?.includes("POB");
        const achievedValue = achieved;
        const percentage = calcPct(achieved, target);

        return (
          <div key={i} style={{ marginBottom: 8 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 5,
                marginBottom: 4,
              }}
            >
              <span
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: "50%",
                  background: entry.fill,
                  display: "inline-block",
                }}
              />
              <span
                style={{
                  color: "#e2e8f0",
                  fontWeight: 600,
                  fontSize: 11,
                }}
              >
                {entry.name}
              </span>
              <span
                style={{
                  marginLeft: "auto",
                  fontWeight: 700,
                  color: statusColor(percentage),
                  background: "rgba(255,255,255,0.1)",
                  padding: "2px 6px",
                  borderRadius: 4,
                }}
              >
                {percentage}%
              </span>
            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                paddingLeft: 13,
                fontSize: 10,
                marginBottom: 2,
                gap: 8,
              }}
            >
              <span style={{ color: "#94a3b8" }}>
                Target:{" "}
                <span style={{ color: "#fbbf24", fontWeight: 600 }}>
                  {fmtNum(target)}
                </span>
              </span>

              <span style={{ color: "#94a3b8" }}>
                {isPOB ? "POB" : "SOB"}:{" "}
                <span style={{ color: "#34d399", fontWeight: 600 }}>
                  {fmtNum(achievedValue)}
                </span>
              </span>
            </div>

            {/* Progress bar */}
            <div
              style={{
                marginLeft: 13,
                marginTop: 2,
                height: 3,
                background: "#334155",
                borderRadius: 2,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  width: `${Math.min(percentage, 100)}%`,
                  height: "100%",
                  background: statusColor(percentage),
                }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
} 

/* ─────────────────────────────────────────────────────────────── */
/* Legend shape renderer */
/* ─────────────────────────────────────────────────────────────── */

function ChartPanel({ title, apiData, loading, headerBg, onFYChange }) {
  const [selectedFY, setSelectedFY] = useState(getCurrentFY());
  const [activeState, setActiveState] = useState(null);

  const rawRows = useMemo(() => {
    const rows = apiData?.[selectedFY];
    return Array.isArray(rows) ? rows : [];
  }, [apiData, selectedFY]);

  const states = useMemo(() => rawRows?.map((r) => r.state) || [], [rawRows]);

  const dynamicMonths = useMemo(() => {
    if (!rawRows.length) return [];

    const apiMonths = Object.keys(rawRows[0].month_data || {});

    return MONTHS.filter((m) => apiMonths.includes(m));
  }, [rawRows]);

  const monthlyData = useMemo(() => {
    return dynamicMonths.map((month) => {
      const entry = { month, _month: month, _rawData: {} };
      rawRows.forEach((row) => {
        const md = row.month_data?.[month];
        entry[row.state] = calcPct(md?.achieved || 0, md?.target || 0);
        entry._rawData[row.state] = {
          target: md?.target || 0,
          achieved: md?.achieved || 0,
        };
      });
      return entry;
    });
  }, [rawRows, dynamicMonths]);
  const availableFYs = FY_OPTIONS;
  const currentMonth =
  dynamicMonths.length > 0
    ? dynamicMonths[dynamicMonths.length - 1]
    : null;
  const fyParts = selectedFY?.split(" ")[1]?.split("-");
  const startYear = fyParts?.[0];
  const endYearShort = fyParts?.[1];
  const endYear = `20${endYearShort}`;
  const currentYear =
    currentMonth === "Jan" || currentMonth === "Feb" || currentMonth === "Mar"
      ? endYear
      : startYear;

  useEffect(() => {
    if (!selectedFY && availableFYs.length > 0) {
      setSelectedFY(availableFYs[0]);
    }
  }, [availableFYs, selectedFY]);
useEffect(() => {
  if (
    selectedFY &&
    !apiData[selectedFY]
  ) {
    onFYChange(selectedFY);
  }
}, [selectedFY]);
  const summary = useMemo(() => {
    let totalTarget = 0;
    let totalAchieved = 0;
    rawRows.forEach((row) => {
      Object.values(row.month_data || {}).forEach((month) => {
        if (month.target > 0 || month.achieved > 0) {
          totalTarget += month.target || 0;
          totalAchieved += month.achieved || 0;
        }
      });
    });
    return {
      totalTarget,
      totalAchieved,
      overallPercentage:
        totalTarget > 0 ? Math.round((totalAchieved / totalTarget) * 100) : 0,
    };
  }, [rawRows]);


  return (
    <div
      style={{
        border: "1px solid #d1d5db",
        borderRadius: 8,
        marginBottom: 20,
        background: "#fff",
        boxShadow: "0 1px 6px rgba(0,0,0,0.08)",
        overflow: "hidden",
        fontFamily: "Segoe UI, Arial, sans-serif",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      {/* Header */}
      <div
        style={{
          background: headerBg,
          padding: "8px 14px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 8,
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span style={{ color: "#fff", fontWeight: 700, fontSize: 13 }}>
            📊 {currentMonth} {currentYear} · {title}
          </span>
          {summary.totalTarget > 0 && (
            <div style={{ display: "flex", gap: 8 }}>
              <span
                style={{
                  background: "rgba(255,255,255,0.15)",
                  padding: "2px 8px",
                  borderRadius: 4,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                Total Target: {fmtNum(summary.totalTarget)}
              </span>
              <span
                style={{
                  background: "rgba(255,255,255,0.15)",
                  padding: "2px 8px",
                  borderRadius: 4,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 600,
                }}
              >
                Total Achieved: {fmtNum(summary.totalAchieved)}
              </span>
              <span
                style={{
                  background: statusColor(summary.overallPercentage),
                  padding: "2px 8px",
                  borderRadius: 4,
                  color: "#fff",
                  fontSize: 10,
                  fontWeight: 700,
                }}
              >
                {summary.overallPercentage}% Overall
              </span>
            </div>
          )}
        </div>

        <div style={{ display: "flex", gap: 5 }}>
          {loading && (
            <span
              style={{
                color: "rgba(255,255,255,0.75)",
                fontSize: 10,
                marginRight: 8,
              }}
            >
              ⟳ Loading...
            </span>
          )}
          <select
            value={selectedFY}
            onChange={(e) => {
              const fy = e.target.value;
              setSelectedFY(fy);
              if (!apiData[fy]) onFYChange(fy);
            }}
            style={{
              padding: "4px 8px",
              borderRadius: 4,
              fontSize: 11,
              fontWeight: 600,
              border: "none",
              cursor: "pointer",
            }}
          >
            {availableFYs.map((fy) => (
              <option key={fy} value={fy}>
                {fy}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        style={{ height: 250, position: "relative", outline: "none" }}
        tabIndex={-1}
      >
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "#64748b",
            textAlign: "center",
            paddingTop: 6,
            letterSpacing: 0.8,
          }}
        >
          {title.includes("POB")
            ? `MONTHLY POB ACHIEVEMENT % · FINANCIAL YEAR: ${selectedFY}`
            : `MONTHLY SOB ACHIEVEMENT % · FINANCIAL YEAR: ${selectedFY}`}
        </div>

        {loading ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              fontSize: 13,
            }}
          >
            Loading...
          </div>
        ) : !rawRows.length ? (
          <div
            style={{
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#64748b",
              fontSize: 13,
            }}
          >
            No data available for {selectedFY}
          </div>
        ) : (
          <>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={monthlyData}
                barCategoryGap="18%"
                barGap={2}
                margin={{ top: 15, right: 8, left: -10, bottom: 40 }}
                style={{ outline: "none" }}
              >
                <CartesianGrid
                  vertical={false}
                  stroke="#e5e7eb"
                  strokeDasharray="0"
                />
                <XAxis
                  dataKey="month"
                  tick={(props) => {
                    const { x, y, payload } = props;

                    const month = payload.value;

                    const targets = rawRows
                      .map((r) => r.month_data?.[month]?.target || 0)
                      .filter(Boolean);

                    const achieveds = rawRows
                      .map((r) => r.month_data?.[month]?.achieved || 0)
                      .filter(Boolean);

                    return (
                      <g transform={`translate(${x},${y})`}>
                        {/* Month */}
                        <text
                          x={0}
                          y={0}
                          dy={10}
                          textAnchor="middle"
                          fontSize={10}
                          fill="#64748b"
                          fontWeight="600"
                        >
                          {payload.value}
                        </text>

                        {/* Target */}
                        <text
                          x={0}
                          y={12}
                          dy={10}
                          textAnchor="middle"
                          fontSize={7}
                          fill="#f59e0b"
                          fontWeight="700"
                        >
                          TGT {targets.map(fmtNum).join(" / ")}
                        </text>

                        {/* POB / Achievement */}
                        <text
                          x={0}
                          y={24}
                          dy={10}
                          textAnchor="middle"
                          fontSize={7}
                          fill="#22c55e"
                          fontWeight="700"
                        >
                          {title.includes("POB") ? "POB" : "ACH"}{" "}
                          {achieveds.map(fmtNum).join(" / ")}
                        </text>
                      </g>
                    );
                  }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, 150]}
                  unit="%"
                  tick={{ fontSize: 9, fill: "#9ca3af" }}
                  tickLine={false}
                />
                <Tooltip
                  content={
                    <CustomTooltip
                      rawRows={rawRows}
                      title={title}
                      selectedFY={selectedFY}
                    />
                  }
                />

                {states.map((state, i) => {
                  const isActive = activeState === state;
                  const color = STATE_COLORS[i % STATE_COLORS.length];

                  return (
                    <Bar
                      key={state}
                      dataKey={state}
                      fill={color}
                      radius={[3, 3, 0, 0]}
                      maxBarSize={16}
                      opacity={activeState && !isActive ? 0.2 : 1}
                      style={{
                        filter: isActive
                          ? `drop-shadow(0px 0px 6px ${color}80)`
                          : "none",
                        transition: "all 0.25s ease",
                      }}
                    >
                      <LabelList
                        position="top"
                        content={(props) => {
                          const { x, y, width, value } = props;
                          if (!value) return null;
                          return (
                            <text
                              x={x + width / 2}
                              y={y - 6}
                              textAnchor="middle"
                              fontSize={9}
                              fill="#111827"
                              fontWeight="600"
                            >
                              {value}%
                            </text>
                          );
                        }}
                      />
                    </Bar>
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </>
        )}
      </div>

      {/* ✅ State color legend BELOW chart */}
      {states.length > 0 && (
        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "center",
            gap: 6,
            padding: "6px 12px 8px",
            borderTop: "1px solid #f1f5f9",
          }}
        >
          {states.map((state, i) => {
            const color = STATE_COLORS[i % STATE_COLORS.length];
            const isActive = activeState === state;
            return (
              <div
                key={state}
                onClick={() =>
                  setActiveState(activeState === state ? null : state)
                }
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 5,
                  padding: "4px 10px 4px 6px",
                  borderRadius: 20,
                  cursor: "pointer",
                  background: isActive ? color : `${color}15`,
                  border: `1.5px solid ${color}`,
                  transition: "all 0.2s ease",
                }}
              >
                <span
                  style={{
                    width: 10,
                    height: 10,
                    borderRadius: "50%",
                    backgroundColor: isActive ? "#fff" : color,
                    display: "inline-block",
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    color: isActive ? "#fff" : color,
                    fontWeight: 700,
                    fontSize: 10,
                    letterSpacing: 0.3,
                    fontFamily: "Segoe UI, Arial, sans-serif",
                  }}
                >
                  {state}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────── */
/* Main Component */
/* ─────────────────────────────────────────────────────────────── */

const CordinatorChart = () => {
  const [pobData, setPobData] = useState({});
  const [sobData, setSobData] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchFYData = async (fyDisplay) => {
    setLoading(true);
    setError(null);
    try {
      const fyParam = fyDisplay.split(" ")[1];
      const [start, endShort] = fyParam.split("-");
      const end = `20${endShort}`;
      const apiFY = `${start}-${end}`;
      const url = `${BASE_URL}/api/dashboard/coordinator?financial_year=${apiFY}`;
      const response = await fetch(url, { headers: getAuthHeaders() });
      if (!response.ok) throw new Error(`HTTP error ${response.status}`);
      const data = await response.json();
      setPobData((prev) => ({ ...prev, [fyDisplay]: data?.pob || [] }));
      setSobData((prev) => ({ ...prev, [fyDisplay]: data?.sob || [] }));
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    const styleEl = document.createElement("style");
    styleEl.textContent = `
    .recharts-wrapper, .recharts-wrapper svg, .recharts-surface {
      outline: none !important;
      border: none !important;
    }
  `;

    if (!document.head.querySelector("[data-recharts-fix]")) {
      styleEl.setAttribute("data-recharts-fix", "true");
      document.head.appendChild(styleEl);
    }
  }, []);
 useEffect(() => {
  fetchFYData(getCurrentFY());
}, []);

  if (error) {
    return (
      <div
        style={{
          padding: "20px",
          textAlign: "center",
          color: "#dc2626",
          background: "#fee2e2",
          borderRadius: 8,
          margin: "8px",
        }}
      >
        Error loading data: {error}
      </div>
    );
  }

  return (
    <div style={{ padding: "8px 4px", width: "100%", boxSizing: "border-box" }}>
      <ChartPanel
        title="State POB Detail — Financial Year Overview"
        apiData={pobData}
        loading={loading}
        headerBg="linear-gradient(90deg, #1d4ed8, #2563eb)"
        onFYChange={fetchFYData}
      />
      <ChartPanel
        title="State SOB Detail — Financial Year Overview"
        apiData={sobData}
        loading={loading}
        headerBg="linear-gradient(90deg, #0f766e, #0d9488)"
        onFYChange={fetchFYData}
      />
    </div>
  );
};

export default CordinatorChart;
